import React, { Fragment, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FiCheck, FiExternalLink, FiHome, FiMail, FiMapPin, FiPackage, FiPhone, FiX } from 'react-icons/fi';
import request from '../../utils/request';
import { PMS_LABELS, fmtMoney, fmtNumber, getOrderRevenue } from './derive';
import { RowSkeleton } from './states';

function DrawerHeader({ kicker, title, subtitle, onClose, badges }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-zinc-100 px-5 py-4">
      <div className="min-w-0">
        {kicker ? (
          <p className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">{kicker}</p>
        ) : null}
        <h2 className="mt-1 truncate text-base font-semibold text-zinc-900">{title}</h2>
        {subtitle ? <p className="mt-0.5 truncate text-xs text-zinc-500">{subtitle}</p> : null}
        {badges ? <div className="mt-2 flex flex-wrap gap-1.5">{badges}</div> : null}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-md p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
        aria-label="Close drawer"
      >
        <FiX className="h-4 w-4" />
      </button>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-[11px] text-zinc-500">{label}</span>
      <span className="text-xs font-semibold text-zinc-900">{value}</span>
    </div>
  );
}

function HostDetail({ host, onClose, onOpenProperty }) {
  const [props, setProps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!host?._id) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [pRes, oRes] = await Promise.all([
          request.get(`/properties/host/${host._id}`).catch(() => ({ data: [] })),
          request.get(`/orders/host/${host._id}`).catch(() => ({ data: [] })),
        ]);
        if (cancel) return;
        const properties = Array.isArray(pRes.data) ? pRes.data : pRes.data?.properties || [];
        const orderList = Array.isArray(oRes.data) ? oRes.data : oRes.data?.orders || [];
        setProps(properties);
        setOrders(orderList);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [host?._id]);

  const revenue = orders.reduce((s, o) => s + getOrderRevenue(o), 0);
  const aov = orders.length ? revenue / orders.length : 0;

  return (
    <>
      <DrawerHeader
        kicker="Host"
        title={host.name}
        subtitle={host.email || host.phoneNumber}
        onClose={onClose}
        badges={
          <>
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${host.active ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {host.active ? 'Active' : 'Inactive'}
            </span>
            {host.pmsType && host.pmsType !== 'unknown' ? (
              <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
                {PMS_LABELS[host.pmsType]}
              </span>
            ) : null}
            <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${host.isNew ? 'bg-emerald-50 text-emerald-700' : 'bg-zinc-100 text-zinc-600'}`}>
              {host.isNew ? 'New host' : 'Legacy host'}
            </span>
          </>
        }
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Properties</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">{fmtNumber(props.length || host.propertyCount || 0)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Orders</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">{fmtNumber(orders.length)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">AOV</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">{fmtMoney(aov)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Volume</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">{fmtMoney(revenue)}</p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-zinc-200 bg-white p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Contact</p>
          <div className="mt-1 space-y-1">
            {host.email ? (
              <div className="flex items-center gap-2 text-xs text-zinc-700"><FiMail className="h-3.5 w-3.5 text-zinc-400" />{host.email}</div>
            ) : null}
            {host.phoneNumber ? (
              <div className="flex items-center gap-2 text-xs text-zinc-700"><FiPhone className="h-3.5 w-3.5 text-zinc-400" />{host.phoneNumber}</div>
            ) : null}
            {host.address?.city ? (
              <div className="flex items-center gap-2 text-xs text-zinc-700">
                <FiMapPin className="h-3.5 w-3.5 text-zinc-400" />
                {host.address.city}{host.address.state ? `, ${host.address.state}` : ''}
              </div>
            ) : null}
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Properties</p>
          {loading ? <RowSkeleton rows={4} /> : props.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-5 text-center text-xs text-zinc-500">No properties.</p>
          ) : (
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
              {props.slice(0, 30).map((p) => (
                <li key={p._id}>
                  <button
                    type="button"
                    onClick={() => onOpenProperty?.(p)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-50"
                  >
                    <FiHome className="h-3.5 w-3.5 text-zinc-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-zinc-900">{p.address?.line1 || p.listingTitle || 'Property'}</p>
                      <p className="truncate text-[10px] text-zinc-500">
                        {p.address?.city || ''}{p.address?.state ? `, ${p.address.state}` : ''}
                        {p.propertySource ? ` · ${PMS_LABELS[p.propertySource] || p.propertySource}` : ''}
                      </p>
                    </div>
                    <FiExternalLink className="h-3.5 w-3.5 text-zinc-300" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-4">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Recent orders</p>
          {loading ? <RowSkeleton rows={3} /> : orders.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-5 text-center text-xs text-zinc-500">No orders.</p>
          ) : (
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
              {orders.slice(0, 10).map((o) => (
                <li key={o._id} className="flex items-center gap-2 px-3 py-2 text-xs">
                  <FiPackage className="h-3.5 w-3.5 text-zinc-400" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-zinc-900">{o.uniqueId || String(o._id).slice(-6)}</p>
                    <p className="truncate text-[10px] text-zinc-500">
                      {o.status} · {o.createdAt ? new Date(o.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </p>
                  </div>
                  <span className="tabular-nums text-zinc-700">{fmtMoney(getOrderRevenue(o))}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  );
}

function PropertyDetail({ property, onClose }) {
  return (
    <>
      <DrawerHeader
        kicker="Property"
        title={property.address?.line1 || property.listingTitle || property.uniqueId || 'Property'}
        subtitle={`${property.address?.city || ''}${property.address?.state ? `, ${property.address.state}` : ''}`}
        onClose={onClose}
        badges={
          property.propertySource ? (
            <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-700">
              {PMS_LABELS[property.propertySource] || property.propertySource}
            </span>
          ) : null
        }
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-3">
          <StatRow label="Source" value={PMS_LABELS[property.propertySource] || property.propertySource || '—'} />
          <StatRow label="Created" value={property.createdAt ? new Date(property.createdAt).toLocaleDateString() : '—'} />
          <StatRow label="ZIP" value={property.address?.zipCode || '—'} />
          <StatRow label="Property ID" value={property._id ? String(property._id).slice(-8) : '—'} />
        </div>
      </div>
    </>
  );
}

function PmsDetail({ pmsId, hosts, properties, propertiesAvailable, onClose, onOpenHost, onOpenProperty }) {
  const list = hosts.filter((h) => h.pmsType === pmsId);
  const matchingProps = (properties || []).filter((p) => p?.propertySource === pmsId);
  const sourceProps = matchingProps
    .slice()
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 50);
  return (
    <>
      <DrawerHeader
        kicker="PMS"
        title={PMS_LABELS[pmsId] || pmsId}
        subtitle={`${fmtNumber(list.length)} hosts · ${propertiesAvailable ? `${fmtNumber(matchingProps.length)} properties` : 'property count unavailable'}`}
        onClose={onClose}
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Hosts</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">{fmtNumber(list.length)}</p>
          </div>
          <div className="rounded-lg border border-zinc-200 bg-white p-3">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Properties</p>
            <p className="mt-1 text-lg font-semibold tabular-nums text-zinc-900">
              {propertiesAvailable ? fmtNumber(matchingProps.length) : '—'}
            </p>
            {!propertiesAvailable ? <p className="text-[10px] text-zinc-500">awaiting backend §B</p> : null}
          </div>
        </div>

        <div className="mt-4">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Hosts on this PMS</p>
          {list.length === 0 ? (
            <p className="rounded-lg border border-dashed border-zinc-200 px-3 py-6 text-center text-xs text-zinc-500">
              No hosts on this PMS in the current filters.
            </p>
          ) : (
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
              {list.slice(0, 100).map((h) => (
                <li key={h._id}>
                  <button
                    type="button"
                    onClick={() => onOpenHost?.(h)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-50"
                  >
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-zinc-100 text-[10px] font-semibold text-zinc-600">
                      {(h.name || '?').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-zinc-900">{h.name}</p>
                      <p className="truncate text-[10px] text-zinc-500">{h.email || '—'} · {h.propertyCount || 0} props</p>
                    </div>
                    {h.active ? <FiCheck className="h-3.5 w-3.5 text-emerald-500" /> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {propertiesAvailable && sourceProps.length > 0 ? (
          <div className="mt-4">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
              Recently added properties on this PMS
            </p>
            <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200">
              {sourceProps.map((p) => (
                <li key={p._id}>
                  <button
                    type="button"
                    onClick={() => onOpenProperty?.(p)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-50"
                  >
                    <FiHome className="h-3.5 w-3.5 text-zinc-400" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-semibold text-zinc-900">{p.address?.line1 || 'Property'}</p>
                      <p className="truncate text-[10px] text-zinc-500">
                        {p.address?.city || ''}{p.address?.state ? `, ${p.address.state}` : ''}
                        {p.createdAt ? ` · added ${new Date(p.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}` : ''}
                      </p>
                    </div>
                    <FiExternalLink className="h-3.5 w-3.5 text-zinc-300" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </>
  );
}

function OrderDetail({ order, onClose }) {
  return (
    <>
      <DrawerHeader
        kicker="Order"
        title={order.uniqueId || String(order._id).slice(-8)}
        subtitle={order.storeLocation || '—'}
        onClose={onClose}
        badges={
          <span className="rounded-md bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-zinc-700">
            {order.status}
          </span>
        }
      />
      <div className="flex-1 overflow-y-auto px-5 py-4">
        <div className="rounded-lg border border-zinc-200 bg-white p-3">
          <StatRow label="Created" value={order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'} />
          <StatRow label="Grocery fee" value={fmtMoney(order.groceryFee || 0)} />
          <StatRow label="Driver fee" value={fmtMoney(order.driverFee || 0)} />
          <StatRow label="Bellhopt fee" value={fmtMoney(order.bellhoptFee || 0)} />
          <StatRow label="Host fee" value={fmtMoney(order.hostFee || 0)} />
          <StatRow label="Tip" value={fmtMoney(order.tip || 0)} />
          <StatRow label="Discount" value={`-${fmtMoney(order.discount || 0)}`} />
          <div className="mt-2 flex items-center justify-between border-t border-zinc-100 pt-2">
            <span className="text-xs font-semibold text-zinc-900">Total</span>
            <span className="text-sm font-bold tabular-nums text-zinc-900">{fmtMoney(getOrderRevenue(order))}</span>
          </div>
        </div>
      </div>
    </>
  );
}

export default function DashboardDrawer({
  open,
  kind,
  payload,
  onClose,
  hosts,
  properties,
  propertiesAvailable,
  onOpenHost,
  onOpenProperty,
}) {
  return (
    <Transition.Root show={Boolean(open)} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="transition-opacity duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-zinc-900/30 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition duration-250"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition duration-200"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-2xl">
                    {kind === 'host' && payload ? (
                      <HostDetail host={payload} onClose={onClose} onOpenProperty={onOpenProperty} />
                    ) : null}
                    {kind === 'property' && payload ? (
                      <PropertyDetail property={payload} onClose={onClose} />
                    ) : null}
                    {kind === 'pms' && payload ? (
                      <PmsDetail
                        pmsId={payload}
                        hosts={hosts || []}
                        properties={properties || []}
                        propertiesAvailable={Boolean(propertiesAvailable)}
                        onClose={onClose}
                        onOpenHost={onOpenHost}
                        onOpenProperty={onOpenProperty}
                      />
                    ) : null}
                    {kind === 'order' && payload ? (
                      <OrderDetail order={payload} onClose={onClose} />
                    ) : null}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
